
import { Component, Vue } from "vue-property-decorator";
import { Attachment, AttachmentMeta, AttachmentType, KnowledgeBaseArticle, KnowledgeBaseArticleStatus, KnowledgeBaseCategory } from "@/survey";

@Component({})
export default class KnowledgeBaseArticleForm extends Vue {
  article: KnowledgeBaseArticle = {
    id: null,
    tableName: "kb_article",
    title: "",
    summary: "",
    content: "",
    categoryId: null,
    tags: "",
    status: "draft"
  };
  categories: KnowledgeBaseCategory[] = [];
  attachments: Attachment[] = [];
  saving = false;
  uploadingAttachment = false;
  deletingAttachmentId: number | null = null;
  error = "";
  apiLocation = Vue.prototype.$apiLocation;
  initialEditorContent = "";
  editorKey = 0;
  selectedFontSize = "";
  selectedTextColor = "#1f2937";
  savedSelection: Range | null = null;

  fontSizeOptions = [
    { value: "", text: "Size" },
    { value: "12px", text: "Small" },
    { value: "16px", text: "Normal" },
    { value: "20px", text: "Large" },
    { value: "28px", text: "Title" }
  ];

  statusOptions: { value: KnowledgeBaseArticleStatus; text: string }[] = [
    { value: "published", text: "Published" },
    { value: "draft", text: "Draft" },
    { value: "private", text: "Private" }
  ];

  get categoryOptions(): { value: number | null; text: string }[] {
    return [
      { value: null, text: "Select Category" },
      ...this.categories.map(category => ({ value: category.id as number, text: category.name }))
    ];
  }

  get visibleAttachments() {
    return this.attachments.filter(attachment => attachment.notes !== "Pasted into article body");
  }

  async created() {
    this.categories = await Vue.prototype.$surveyClient.getKnowledgeBaseCategories(true);
    if (this.$route.params.id) {
      this.article = await Vue.prototype.$surveyClient.getKnowledgeBaseArticle(parseInt(this.$route.params.id));
      this.seedEditorContent();
      this.seedAttachmentsFromArticle();
      await this.loadAttachments(true);
    } else if (this.categories.length > 0) {
      this.article.categoryId = this.categories[0].id as number;
      this.seedEditorContent();
    } else {
      this.seedEditorContent();
    }
  }

  seedEditorContent() {
    this.initialEditorContent = this.normalizeContentForHtml(this.article.content || "");
    this.editorKey++;
  }

  seedAttachmentsFromArticle() {
    this.attachments = (this.article.attachmentMeta || []).map((attachment: AttachmentMeta) => ({ ...attachment }));
  }

  setEditorContent() {
    const editor = this.$refs.editor as HTMLElement;
    if (editor && editor.innerHTML !== (this.article.content || "")) {
      editor.innerHTML = this.normalizeContentForHtml(this.article.content || "");
    }
  }

  syncContent() {
    const editor = this.$refs.editor as HTMLElement;
    if (editor) {
      this.article.content = this.sanitizeHtml(this.normalizeContentForHtml(editor.innerHTML)).trim();
      this.saveSelection();
    }
  }

  normalizeContentForHtml(content: string) {
    if (!content || /<[^>]+>/.test(content)) {
      return content;
    }
    return content
      .split(/\n{2,}/)
      .map(paragraph => paragraph.trim())
      .filter(paragraph => paragraph.length > 0)
      .map(paragraph => `<p>${this.escapeHtml(paragraph).replace(/\n/g, "<br>")}</p>`)
      .join("");
  }

  escapeHtml(content: string) {
    return content
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  sanitizeHtml(html: string) {
    const template = document.createElement("template");
    template.innerHTML = html;
    template.content.querySelectorAll("script, iframe, object, embed").forEach(element => element.remove());
    template.content.querySelectorAll("*").forEach((element: Element) => {
      Array.from(element.attributes).forEach(attribute => {
        const name = attribute.name.toLowerCase();
        const value = attribute.value.trim().toLowerCase();
        if (name.startsWith("on") || name === "dir" || value.startsWith("javascript:") || value.indexOf("direction:") >= 0) {
          element.removeAttribute(attribute.name);
        }
      });
    });
    return template.innerHTML;
  }

  focusEditor() {
    const editor = this.$refs.editor as HTMLElement;
    if (editor) {
      editor.focus();
    }
  }

  saveSelection() {
    const selection = window.getSelection();
    const editor = this.$refs.editor as HTMLElement;
    if (!selection || selection.rangeCount === 0 || !editor) {
      return;
    }
    const range = selection.getRangeAt(0);
    if (editor.contains(range.commonAncestorContainer)) {
      this.savedSelection = range.cloneRange();
    }
  }

  restoreSelection() {
    const selection = window.getSelection();
    if (!selection || this.savedSelection === null) {
      return;
    }
    selection.removeAllRanges();
    selection.addRange(this.savedSelection);
  }

  format(command: string) {
    this.focusEditor();
    this.restoreSelection();
    document.execCommand(command, false);
    this.syncContent();
  }

  formatBlock(block: string) {
    this.focusEditor();
    this.restoreSelection();
    document.execCommand("formatBlock", false, block);
    this.syncContent();
  }

  applyFontSize() {
    if (!this.selectedFontSize) {
      return;
    }
    this.wrapSelectionWithStyle("fontSize", this.selectedFontSize);
  }

  applyTextColor() {
    this.wrapSelectionWithStyle("color", this.selectedTextColor);
  }

  wrapSelectionWithStyle(styleName: "fontSize" | "color", value: string) {
    this.focusEditor();
    this.restoreSelection();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      this.syncContent();
      return;
    }
    const range = selection.getRangeAt(0);
    const span = document.createElement("span");
    span.style[styleName] = value;
    span.appendChild(range.extractContents());
    range.insertNode(span);
    selection.removeAllRanges();
    const nextRange = document.createRange();
    nextRange.selectNodeContents(span);
    selection.addRange(nextRange);
    this.syncContent();
  }

  insertLink() {
    const url = prompt("Link URL");
    if (url) {
      this.focusEditor();
      this.restoreSelection();
      document.execCommand("createLink", false, url);
      this.syncContent();
    }
  }

  async ensureSavedForAttachment() {
    this.syncContent();
    if (this.article.id) {
      return true;
    }
    if (!this.article.title.trim() || this.article.categoryId === null) {
      this.error = "Enter a title and category before adding attachments or pasted images.";
      return false;
    }
    if (!this.article.content.trim()) {
      this.article.content = "<p></p>";
    }
    const saved = await Vue.prototype.$surveyClient.createOrUpdateKnowledgeBaseArticle(this.article);
    this.article = saved;
    return true;
  }

  async handlePaste(event: ClipboardEvent) {
    const items = event.clipboardData ? Array.from(event.clipboardData.items) : [];
    const imageItems = items.filter(item => ["image/jpeg", "image/png", "image/gif"].indexOf(item.type) >= 0);
    if (imageItems.length === 0) {
      return;
    }
    event.preventDefault();
    const canUpload = await this.ensureSavedForAttachment();
    if (!canUpload || !this.article.id) {
      return;
    }
    for (const item of imageItems) {
      const file = item.getAsFile();
      if (file) {
        await this.uploadInlineImage(file);
      }
    }
  }

  async uploadInlineImage(file: File) {
    this.uploadingAttachment = true;
    try {
      await Vue.prototype.$surveyClient.uploadAttachment({
        file,
        uploadName: file.name,
        tableName: "kb_article",
        recordId: "" + this.article.id,
        attachmentType: AttachmentType.IMAGE,
        notes: "Pasted into article body"
      });
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        if (typeof event.target?.result === "string") {
          document.execCommand("insertImage", false, event.target.result);
          this.syncContent();
        }
      };
      reader.readAsDataURL(file);
      await this.loadAttachments();
    } finally {
      this.uploadingAttachment = false;
    }
  }

  async uploadAttachments(files: File[] | FileList | null) {
    if (!files || files.length === 0) {
      return;
    }
    const canUpload = await this.ensureSavedForAttachment();
    if (!canUpload || !this.article.id) {
      return;
    }
    this.uploadingAttachment = true;
    try {
      for (const file of Array.from(files)) {
        await Vue.prototype.$surveyClient.uploadAttachment({
          file,
          uploadName: file.name,
          tableName: "kb_article",
          recordId: "" + this.article.id,
          attachmentType: file.type.indexOf("image/") === 0 ? AttachmentType.IMAGE : AttachmentType.OTHER,
          notes: "Knowledge base attachment"
        });
      }
      await this.loadAttachments();
    } finally {
      this.uploadingAttachment = false;
    }
  }

  async loadAttachments(preserveExisting = false) {
    if (!this.article.id) {
      this.attachments = [];
      return;
    }
    try {
      const attachments = await Vue.prototype.$surveyClient.getAttachments("kb_article", this.article.id);
      if (!preserveExisting || attachments.length > 0 || this.attachments.length === 0) {
        this.attachments = attachments;
      }
    } catch (error) {
      if (!preserveExisting) {
        this.error = "Unable to load article attachments.";
      }
    }
  }

  async deleteAttachment(id: number | undefined) {
    if (!id || !this.article.id || !confirm("Delete this attachment?")) {
      return;
    }
    this.deletingAttachmentId = id;
    this.error = "";
    try {
      this.attachments = this.attachments.filter(attachment => attachment.id !== id);
      await Vue.prototype.$surveyClient.deleteKnowledgeBaseArticleAttachment(this.article.id, id);
      await this.loadAttachments();
    } catch (error) {
      this.error = "Unable to delete the attachment.";
      await this.loadAttachments(true);
    } finally {
      this.deletingAttachmentId = null;
    }
  }

  async downloadAttachment(attachment: Attachment) {
    if (!attachment.id) {
      return;
    }
    const blob = await Vue.prototype.$surveyClient.downloadAttachment(attachment.id, "kb_article");
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = attachment.name || attachment.originalfilename || "knowledge-base-attachment";
    link.click();
    window.URL.revokeObjectURL(url);
  }

  isAttachmentBusy(id: number | undefined) {
    return id !== undefined && this.deletingAttachmentId === id;
  }

  formatBytes(value: number) {
    if (!value || value <= 0) {
      return "0 B";
    }
    const units = ["B", "KB", "MB", "GB", "TB"];
    let size = value;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size = size / 1024;
      unitIndex += 1;
    }
    const digits = size >= 10 || unitIndex === 0 ? 0 : 1;
    return `${size.toFixed(digits)} ${units[unitIndex]}`;
  }

  async onSubmit() {
    this.error = "";
    this.syncContent();
    if (!this.article.title.trim() || !this.article.content.trim() || this.article.categoryId === null) {
      this.error = "Title, category, and content are required.";
      return;
    }
    this.saving = true;
    try {
      const saved = await Vue.prototype.$surveyClient.createOrUpdateKnowledgeBaseArticle(this.article);
      this.$router.push("/knowledge-base/article/" + saved.id);
    } catch (error) {
      this.error = "Unable to save the knowledge base article.";
    } finally {
      this.saving = false;
    }
  }

  onCancel() {
    if (this.article.id) {
      this.$router.push("/knowledge-base/article/" + this.article.id);
    } else {
      this.$router.push("/knowledge-base");
    }
  }
}
